import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

export interface OnboardingStatus {
  completed: boolean;
  completed_at: string | null;
  has_provider_key: boolean;
  has_gateway_key: boolean;
  /** For a Customer that signed up on its own, onboarding completes when this
   * turns true. */
  first_request_seen: boolean;
  lane: "self_serve" | "enterprise";
  /** Signed up on its own (Self-serve, or Priority: ADR-0004): the wizard. */
  self_signup: boolean;
  /** A Priority Customer's Benchmark length in days; null otherwise. */
  benchmark_days: number | null;
  /** The gateway's public address, for the integration snippets. */
  api_base_url: string;
  /** The Free week's configured length (shortened on staging). */
  free_week_minutes: number;
}

export interface AcceptedModel {
  id: string;
  provider: string;
}

export const ONBOARDING_KEY = ["onboarding-status"];
/** How often the wizard checks whether the first request has landed. */
export const FIRST_REQUEST_POLL_MS = 3000;

/** True while a Customer that signed up on its own has a gateway key but no
 * request yet. */
export function awaitingFirstRequest(status: OnboardingStatus | undefined): boolean {
  return Boolean(status && status.self_signup && status.has_gateway_key && !status.first_request_seen);
}

export function useOnboarding(enabled: boolean) {
  return useQuery({
    queryKey: ONBOARDING_KEY,
    queryFn: () => api.get("onboarding").json<OnboardingStatus>(),
    enabled,
    // Re-check when the tab regains focus so the guide reflects a key the user
    // just added on the Settings page.
    refetchOnWindowFocus: true,
    // The wizard's last step waits live for the first request.
    refetchInterval: (query) => (awaitingFirstRequest(query.state.data) ? FIRST_REQUEST_POLL_MS : false),
  });
}

/** The model names the gateway accepts (as GET /v1/models lists them). */
export function useAcceptedModels(enabled: boolean) {
  return useQuery({
    queryKey: ["onboarding-models"],
    queryFn: () => api.get("onboarding/models").json<{ models: AcceptedModel[] }>(),
    enabled,
    staleTime: Infinity,
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("onboarding/complete").json<OnboardingStatus>(),
    onSuccess: (data) => {
      qc.setQueryData(ONBOARDING_KEY, data);
    },
  });
}
